import { differenceInCalendarDays, format, isValid, parse } from "date-fns";

import type { SessionMetricPoint } from "api/types/charts";
import type { MarkerTypeApi, TargetMarker } from "api/types/markers";
import type { ChartMarkerLine } from "../Targets/TargetChart";

/**
 * Chart line style per marker type. Typed as a full `Record<MarkerTypeApi, …>`
 * so adding a marker type to the union without a variant here is a compile
 * error rather than a silent misclassification. Unknown runtime values resolve
 * to `undefined` and are skipped.
 */
const VARIANT_BY_TYPE: Record<MarkerTypeApi, ChartMarkerLine["variant"]> = {
  phase_change: "phase-change",
  external_event: "external-event",
};

/** Date format the markers API reads and writes (date-only). */
const API_DATE_FORMAT = "yyyy-MM-dd";

/** A plotted session that has a date, with its position in the series. */
interface DatedSession {
  index: number;
  /** UTC calendar day, as `yyyy-MM-dd`. */
  day: string;
}

/**
 * UTC calendar day of an ISO timestamp, as `yyyy-MM-dd`.
 *
 * Markers are stored on the UTC day of the session they were created from, so
 * sessions are read the same way — the local day would put a late-evening
 * session on the day before its own marker.
 */
const toUtcDay = (timestamp: string | null | undefined): string | null => {
  if (!timestamp) return null;
  const date = new Date(timestamp);
  return isValid(date) ? date.toISOString().slice(0, 10) : null;
};

/**
 * A `yyyy-MM-dd` day as the charts write dates, e.g. "July 8th, 2026". Parsed
 * as a local date, so the day shown is the day stored (never via `new Date(iso)`,
 * which would shift it west of UTC).
 */
const toDateLabel = (day: string): string =>
  format(parse(day, API_DATE_FORMAT, new Date()), "MMMM do, yyyy");

/** Days between two `yyyy-MM-dd` days, parsed as local dates so no offset applies. */
const daysBetween = (from: string, to: string): number =>
  differenceInCalendarDays(
    parse(to, API_DATE_FORMAT, new Date()),
    parse(from, API_DATE_FORMAT, new Date())
  );

/**
 * X position of a date-linked marker among the plotted sessions, or null when
 * it can't be placed.
 *
 * - Sessions recorded that day: the marker sits on one of them — the last for a
 *   phase line, so it falls after everything recorded under the old conditions,
 *   the first for an external event, where the event happened.
 * - No session that day: the position is interpolated between the neighbouring
 *   sessions, proportionally to how far the date sits between their days, so
 *   the line lands where the date actually falls.
 * - After the last dated session: half a category past it, the space the x axis
 *   reserves with `offset: true`.
 * - Before the first one: not placed — there's nothing to read it against.
 *
 * @param day      - The marker's UTC day, `yyyy-MM-dd`
 * @param variant  - Resolved line variant, which decides same-day ties
 * @param sessions - Plotted sessions that have a date, oldest first
 */
function resolveDateIndex(
  day: string,
  variant: ChartMarkerLine["variant"],
  sessions: DatedSession[]
): number | null {
  const sameDay = sessions.filter((session) => session.day === day);
  if (sameDay.length > 0) {
    return variant === "phase-change" ? sameDay[sameDay.length - 1].index : sameDay[0].index;
  }

  const previous = [...sessions].reverse().find((session) => session.day < day);
  const next = sessions.find((session) => session.day > day);

  if (!previous) return null;
  // Past the last session: the axis reserves half a category on each side.
  if (!next) return previous.index + 0.5;

  const span = daysBetween(previous.day, next.day);
  if (span <= 0) return previous.index;
  const fraction = daysBetween(previous.day, day) / span;
  return previous.index + fraction * (next.index - previous.index);
}

/** Position of the session a marker was created from, when it's still plotted. */
const sessionIndex = (
  marker: TargetMarker,
  indexBySessionId: Map<number, number>
): number | null =>
  marker.target_session_id == null
    ? null
    : (indexBySessionId.get(marker.target_session_id) ?? null);

/**
 * Resolves persisted markers to chart x-positions.
 *
 * The chart plots one point per session in array order, so a marker's position
 * is an index into `metrics` (parallel to the chart's labels), not a session
 * id. Markers are placed by `marker_date` (see {@link resolveDateIndex}), which
 * can land between two sessions and so be fractional — the category scale
 * interpolates those.
 *
 * A marker with no `marker_date` at all — from a backend that predates marker
 * dates — falls back to the session it was created from. Anything that can't be
 * placed is skipped rather than drawn somewhere wrong: a date earlier than every
 * session (its own fallback doesn't apply, the date is what's being honoured), a
 * session missing from the current series, an unknown marker type.
 *
 * @param markers - Persisted markers for the target.
 * @param metrics - Session metric points, oldest first (parallel to the chart).
 */
export function resolveChartMarkers(
  markers: TargetMarker[],
  metrics: SessionMetricPoint[]
): ChartMarkerLine[] {
  const indexBySessionId = new Map<number, number>();
  const datedSessions: DatedSession[] = [];
  metrics.forEach((point, index) => {
    indexBySessionId.set(point.target_session_id, index);
    const day = toUtcDay(point.session_date);
    if (day != null) datedSessions.push({ index, day });
  });

  const resolved: ChartMarkerLine[] = [];
  for (const marker of markers) {
    const variant = VARIANT_BY_TYPE[marker.marker_type];
    if (!variant) continue;

    const day = toUtcDay(marker.marker_date);
    // Older API, or a chart whose sessions have no dates: the session the
    // marker was created from still places it.
    const index =
      day != null && datedSessions.length > 0
        ? resolveDateIndex(day, variant, datedSessions)
        : sessionIndex(marker, indexBySessionId);
    if (index == null) continue;

    resolved.push({
      id: marker.id,
      index,
      variant,
      label: marker.label,
      // Copy for the chart's hover tooltip. Blank values are left out so the
      // tooltip can drop those rows.
      ...(day != null && { dateLabel: toDateLabel(day) }),
      ...(marker.notes?.trim() && { notes: marker.notes.trim() }),
      appliedTargetCount: marker.target_ids?.length ?? 1,
    });
  }
  return resolved;
}
